import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  Container,
  Heading,
  Text,
  Button,
  HStack,
  VStack,
  Box,
  Card,
  Link,
  SimpleGrid,
  Badge,
} from "@chakra-ui/react";
import {
  LuUsers,
  LuReceipt,
  LuChartBar,
  LuEqual,
  LuPercent,
  LuShare2,
  LuArrowRight,
  LuWallet,
} from "react-icons/lu";
import { Logo } from "@/components/logo";
import { CURRENCY_LABELS, CurrencyIcon, SUPPORTED_CURRENCIES } from "@/lib/constants";
import { getAuthUser } from "@/lib/server-fns";
import { getVersionMetadata } from "@/routes/__root";

export const Route = createFileRoute("/")({
  beforeLoad: async () => {
    const user = await getAuthUser();
    if (user) {
      throw redirect({ to: "/dashboard" });
    }
  },
  loader: async () => {
    try {
      return await getVersionMetadata();
    } catch {
      return { id: "", tag: "dev" };
    }
  },
  head: () => ({
    links: [{ rel: "canonical", href: "https://loonie.app/" }],
  }),
  component: Home,
});

const SPLIT_FEATURES = [
  {
    icon: <LuEqual size={20} />,
    title: "Split equally",
    description:
      "Divide the total evenly across everyone involved — the simplest way to handle a group dinner or a shared bill.",
  },
  {
    icon: <LuWallet size={20} />,
    title: "Exact amounts",
    description:
      "Enter precisely what each person owes. Ideal when someone ordered more than the rest of the table.",
  },
  {
    icon: <LuPercent size={20} />,
    title: "By percentage",
    description:
      "Give each person a percentage of the total. Loonie checks that the percentages add up to 100%.",
  },
  {
    icon: <LuShare2 size={20} />,
    title: "By shares",
    description:
      "Assign share counts and the total is divided proportionally. Great for uneven ratios like 2:1.",
  },
];

const APP_FEATURES = [
  {
    icon: <LuUsers size={20} />,
    title: "Friends",
    description:
      "Send and accept friend requests, then see exactly where you stand with each person at a glance.",
  },
  {
    icon: <LuChartBar size={20} />,
    title: "Live balances",
    description:
      "Always know who owes what. Every expense and settlement updates your balances immediately.",
  },
  {
    icon: <LuReceipt size={20} />,
    title: "Settlements",
    description:
      "Record a payment when you settle up with a friend and watch the outstanding balance adjust automatically.",
  },
];

function FeatureIcon({ children }: { children: React.ReactNode }) {
  return (
    <Box bg="loonie.subtle" color="loonie.fg" p="2" borderRadius="md" display="inline-flex">
      {children}
    </Box>
  );
}

function Home() {
  const { tag } = Route.useLoaderData();

  return (
    <Box minH="100dvh">
      {/* Hero */}
      <Box as="header" py={{ base: "16", md: "24" }} px={{ base: "6", md: "0" }}>
        <Container maxW="2xl">
          <VStack align="center" gap={{ base: "8", md: "10" }} textAlign="center">
            <Logo size="lg" />
            <VStack gap={{ base: "4", md: "5" }}>
              <Heading
                as="h1"
                size={{ base: "4xl", md: "6xl" }}
                fontWeight="bold"
                lineHeight="tight"
              >
                Split expenses without the awkwardness
              </Heading>
              <Text fontSize={{ base: "lg", md: "xl" }} color="fg.muted" maxW="lg">
                Track shared costs with friends and roommates. Split every expense equally, by exact
                amounts, by percentage, or by custom shares — in whichever currency you spent.
              </Text>
            </VStack>
            <VStack gap="3">
              <Button asChild size="lg" colorPalette="loonie" px="8">
                <a href="/login">
                  Get started free
                  <LuArrowRight />
                </a>
              </Button>
              <Text fontSize="sm" color="fg.subtle">
                Free to use. Sign in with your Google account.
              </Text>
            </VStack>
          </VStack>
        </Container>
      </Box>

      <Box as="main">
        {/* Split types */}
        <Box
          as="section"
          aria-labelledby="split-types"
          py={{ base: "12", md: "16" }}
          px={{ base: "6", md: "0" }}
          bg="bg.subtle"
        >
          <Container maxW="3xl">
            <VStack gap={{ base: "8", md: "12" }}>
              <VStack gap="3" textAlign="center">
                <Heading as="h2" id="split-types" size={{ base: "xl", md: "2xl" }}>
                  Four ways to split
                </Heading>
                <Text color="fg.muted" fontSize={{ base: "md", md: "lg" }}>
                  Every situation is different. Loonie adapts to how your group wants to split.
                </Text>
              </VStack>

              <SimpleGrid columns={{ base: 1, sm: 2 }} gap={{ base: "4", md: "6" }} w="100%">
                {SPLIT_FEATURES.map((feature) => (
                  <Card.Root key={feature.title} variant="outline">
                    <Card.Body p={{ base: "5", md: "6" }}>
                      <VStack align="start" gap="3">
                        <FeatureIcon>{feature.icon}</FeatureIcon>
                        <VStack align="start" gap="1">
                          <Heading as="h3" size="sm" fontWeight="semibold">
                            {feature.title}
                          </Heading>
                          <Text color="fg.muted" fontSize="sm">
                            {feature.description}
                          </Text>
                        </VStack>
                      </VStack>
                    </Card.Body>
                  </Card.Root>
                ))}
              </SimpleGrid>
            </VStack>
          </Container>
        </Box>

        {/* Multi-currency */}
        <Box
          as="section"
          aria-labelledby="multi-currency"
          py={{ base: "12", md: "16" }}
          px={{ base: "6", md: "0" }}
        >
          <Container maxW="3xl">
            <VStack gap={{ base: "8", md: "12" }}>
              <VStack gap="3" textAlign="center">
                <Heading as="h2" id="multi-currency" size={{ base: "xl", md: "2xl" }}>
                  Multi-currency support
                </Heading>
                <Text color="fg.muted" fontSize={{ base: "md", md: "lg" }}>
                  Traveling abroad or splitting with friends overseas? Record expenses in CAD, USD,
                  EUR, or GBP, and Loonie keeps a separate balance for each one.
                </Text>
              </VStack>

              <HStack gap="4" flexWrap="wrap" justify="center">
                {SUPPORTED_CURRENCIES.map((currency) => (
                  <Card.Root key={currency} variant="outline" minW="32">
                    <Card.Body p="5">
                      <VStack gap="2" align="center">
                        <Box color="loonie.fg">
                          <CurrencyIcon currency={currency} size={24} />
                        </Box>
                        <Badge colorPalette="loonie" variant="subtle">
                          {currency}
                        </Badge>
                        <Text fontSize="xs" color="fg.muted" textAlign="center">
                          {CURRENCY_LABELS[currency]}
                        </Text>
                      </VStack>
                    </Card.Body>
                  </Card.Root>
                ))}
              </HStack>
            </VStack>
          </Container>
        </Box>

        {/* Additional features */}
        <Box
          as="section"
          aria-labelledby="everything"
          py={{ base: "12", md: "16" }}
          px={{ base: "6", md: "0" }}
          bg="bg.subtle"
        >
          <Container maxW="3xl">
            <VStack gap={{ base: "8", md: "12" }}>
              <Heading as="h2" id="everything" size={{ base: "xl", md: "2xl" }} textAlign="center">
                Everything in one place
              </Heading>

              <SimpleGrid columns={{ base: 1, md: 3 }} gap={{ base: "6", md: "8" }} w="100%">
                {APP_FEATURES.map((feature) => (
                  <VStack key={feature.title} align="start" gap="3">
                    <FeatureIcon>{feature.icon}</FeatureIcon>
                    <VStack align="start" gap="1">
                      <Heading as="h3" size="sm">
                        {feature.title}
                      </Heading>
                      <Text color="fg.muted" fontSize="sm">
                        {feature.description}
                      </Text>
                    </VStack>
                  </VStack>
                ))}
              </SimpleGrid>
            </VStack>
          </Container>
        </Box>

        {/* Bottom CTA */}
        <Box
          as="section"
          aria-labelledby="get-started"
          py={{ base: "16", md: "24" }}
          px={{ base: "6", md: "0" }}
        >
          <Container maxW="lg">
            <VStack align="center" gap={{ base: "6", md: "8" }} textAlign="center">
              <VStack gap="3">
                <Heading as="h2" id="get-started" size={{ base: "2xl", md: "3xl" }}>
                  Ready to split smarter?
                </Heading>
                <Text color="fg.muted" fontSize={{ base: "md", md: "lg" }}>
                  Sign in with Google and start tracking shared expenses in seconds.
                </Text>
              </VStack>
              <Button asChild size="lg" colorPalette="loonie" px="8">
                <a href="/login">
                  Get started free
                  <LuArrowRight />
                </a>
              </Button>
            </VStack>
          </Container>
        </Box>
      </Box>

      {/* Footer */}
      <Box as="footer" py="6" px={{ base: "6", md: "0" }} borderTopWidth="1px" borderColor="border">
        <Container maxW="3xl">
          <VStack gap="1">
            <HStack gap="2" fontSize="sm" color="fg.muted">
              <Text>© {new Date().getFullYear()} Loonie</Text>
              <Text aria-hidden="true">·</Text>
              <Link href="/privacy" color="inherit">
                Privacy Policy
              </Link>
            </HStack>
            <Text fontSize="2xs" color="fg.subtle" fontFamily="mono">
              {tag}
            </Text>
          </VStack>
        </Container>
      </Box>
    </Box>
  );
}
